import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface FeedbackFormProps {
  source?: string;
  metadata?: Record<string, unknown>;
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

export function FeedbackForm({ source = 'general', metadata, onSuccess, onCancel, className = '' }: FeedbackFormProps) {
  const intl = useIntl();
  const { isAuthenticated } = useAuth();
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage(null);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: feedback.trim(),
          source,
          metadata,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit feedback');
      }

      setSubmitStatus('success');
      setFeedback('');
      
      if (onSuccess) {
        onSuccess();
      }

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Allow anonymous feedback - no authentication required

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${className}`}>
      <div>
        <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          <FormattedMessage
            id="feedback.label"
            defaultMessage="Your Feedback"
            description="Label for feedback textarea"
          />
        </label>
        <textarea
          id="feedback-message"
          className="w-full px-4 py-3 border border-gray-300/60 dark:border-gray-600/60 rounded-2xl bg-gray-50/50 dark:bg-gray-800/50 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-blue-500/50 dark:focus:border-blue-400/50 resize-none transition-all duration-200"
          rows={5}
          placeholder={intl.formatMessage({
            id: 'feedback.placeholder',
            defaultMessage: 'Tell us what we should change or add...',
            description: 'Placeholder text for feedback textarea',
          })}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isSubmitting}
          required
        />
      </div>

      {submitStatus === 'success' && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <FormattedMessage
            id="feedback.success"
            defaultMessage="Thank you! Your feedback has been submitted."
            description="Success message after submitting feedback"
          />
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-2xl">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            {errorMessage || intl.formatMessage({
              id: 'feedback.error',
              defaultMessage: 'Failed to submit feedback. Please try again.',
              description: 'Error message when feedback submission fails',
            })}
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
          >
            <FormattedMessage
              id="feedback.cancel"
              defaultMessage="Cancel"
              description="Cancel button text"
            />
          </button>
        )}
        <button
          type="submit"
          disabled={!feedback.trim() || isSubmitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 dark:focus:ring-offset-gray-900 shadow-sm hover:shadow-md disabled:shadow-none"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <FormattedMessage
                id="feedback.submitting"
                defaultMessage="Submitting..."
                description="Button text when submitting feedback"
              />
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <FormattedMessage
                id="feedback.submit"
                defaultMessage="Submit"
                description="Button text for submitting feedback"
              />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
